/*
 * lib/setup-gate.js
 *
 * Welkomstscherm dat verschijnt na een succesvolle login wanneer de
 * gebruiker nog geen huishouden heeft (profile.household_id is null).
 *
 * Drie stappen:
 *   1. Welkom + display-name + keuze tussen "nieuw huishouden" of
 *      "aansluiten bij bestaand".
 *   2a. Nieuw huishouden → naam invullen → maak aan → toon invite-link
 *       om door te sturen naar partner.
 *   2b. Aansluiten → invite-code/UUID invullen (of auto-overgenomen
 *       uit de ?invite=URL-param) → bevestig.
 *   3. Klaar → callback voert de gebruiker door naar de app.
 *
 * Schrijfacties:
 *   - createHousehold(name)  → nieuwe households-rij
 *   - updateProfile({ display_name, household_id })
 *   - bij join: enkel updateProfile({ display_name, household_id })
 */

import { signOut } from './auth.js';
import {
  setupNewHousehold,
  fetchHousehold,
  updateProfile
} from './supabase.js';

/**
 * Mount de setup-gate. `profile` is de huidige profile-row (display_name,
 * household_id). `inviteFromUrl` is een eventuele household-UUID die uit
 * de URL of localStorage komt — zo kan de partner-flow auto-gepre-fild.
 * Roept `onDone(updatedProfile, household)` zodra alles is opgeslagen.
 */
export function mountSetupGate(profile, inviteFromUrl, onDone) {
  const app = document.getElementById('app');

  // Default display name: huidige value, of het email-local-part dat de
  // trigger oorspronkelijk invulde. We tonen 'm zodat de gebruiker eenvoudig
  // kan bevestigen / overschrijven.
  const defaultName = profile?.display_name || '';

  // State binnen de gate. Kan: 'choose' | 'create' | 'join' | 'invite-shown'.
  // Als er een invite uit de URL komt → start meteen in 'join' met die ingevuld.
  let view = inviteFromUrl ? 'join' : 'choose';
  let chosenName = defaultName;
  let createdHousehold = null;   // gevuld na succes-create, voor invite-stap

  function render() {
    app.innerHTML = `
      <div class="auth-gate">
        <div class="auth-card setup-card">
          <img src="/favicon.svg" alt="Florijn-logo" class="auth-logo" width="72" height="72">
          ${renderViewContent()}
        </div>
        <button class="setup-signout-link" id="setup-signout">Uitloggen</button>
      </div>`;
    bindHandlers();
  }

  function renderViewContent() {
    if (view === 'choose') {
      return `
        <h1 class="auth-title">Welkom bij Florijn</h1>
        <p class="auth-subtitle">Eén keer instellen, daarna ben je klaar.</p>
        <form id="setup-form-choose" class="auth-form">
          <label for="setup-name" class="auth-label">Hoe wil je heten in de app?</label>
          <input id="setup-name" class="auth-input" type="text"
                 placeholder="Bv. je voornaam" autocomplete="given-name"
                 value="${escapeAttr(chosenName)}" required>
          <p class="setup-hint">Zichtbaar voor jou en je medebewoner.</p>

          <div class="setup-choice-group">
            <button type="button" class="setup-choice-btn" data-action="go-create">
              <strong>Nieuw huishouden starten</strong>
              <span>Begin een nieuw Florijn-budget voor jullie samen.</span>
            </button>
            <button type="button" class="setup-choice-btn" data-action="go-join">
              <strong>Aansluiten bij bestaand huishouden</strong>
              <span>Je medebewoner heeft je een uitnodiging gestuurd.</span>
            </button>
          </div>
        </form>`;
    }

    if (view === 'create') {
      return `
        <h1 class="auth-title">Nieuw huishouden</h1>
        <p class="auth-subtitle">Geef je huishouden een naam — zo herken je 'm later terug.</p>
        <form id="setup-form-create" class="auth-form">
          <label for="setup-household-name" class="auth-label">Naam van het huishouden</label>
          <input id="setup-household-name" class="auth-input" type="text"
                 placeholder="Bv. ${escapeAttr(chosenName || 'Jij')} &amp; co" autocomplete="off"
                 value="${escapeAttr(suggestHouseholdName(chosenName))}" required>

          <button type="submit" class="auth-btn">Maak aan</button>
          <button type="button" class="auth-btn-secondary" data-action="back">Terug</button>
        </form>
        <p class="auth-msg" id="setup-msg" role="status" aria-live="polite"></p>`;
    }

    if (view === 'join') {
      const inviteValue = inviteFromUrl || '';
      return `
        <h1 class="auth-title">Aansluiten bij huishouden</h1>
        <p class="auth-subtitle">${inviteFromUrl
          ? 'Je bent uitgenodigd — bevestig hieronder.'
          : 'Plak je uitnodigingscode of -link.'}</p>
        <form id="setup-form-join" class="auth-form">
          <label for="setup-invite" class="auth-label">Uitnodigingscode</label>
          <input id="setup-invite" class="auth-input" type="text"
                 placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                 value="${escapeAttr(inviteValue)}"
                 spellcheck="false" autocomplete="off" required>
          <p class="setup-hint">Tip: je kan ook de hele uitnodigingslink plakken — we halen de code er zelf uit.</p>

          <button type="submit" class="auth-btn">Aansluiten</button>
          <button type="button" class="auth-btn-secondary" data-action="back">Terug</button>
        </form>
        <p class="auth-msg" id="setup-msg" role="status" aria-live="polite"></p>`;
    }

    if (view === 'invite-shown') {
      const link = inviteLinkFor(createdHousehold.id);
      return `
        <h1 class="auth-title">🎉 Huishouden klaar!</h1>
        <p class="auth-subtitle">Stuur deze link naar je medebewoner — daarmee kan hij of zij aansluiten.</p>
        <div class="setup-invite-block">
          <input id="setup-invite-link" class="auth-input setup-invite-link" type="text" readonly value="${escapeAttr(link)}">
          <button type="button" class="auth-btn-secondary" data-action="copy-link">Kopieer link</button>
        </div>
        <p class="setup-hint">Je vindt de link later ook terug in de instellingen — geen paniek als je 'm nu kwijt geraakt.</p>

        <button type="button" class="auth-btn" data-action="finish">Door naar Florijn</button>`;
    }

    return '<p class="auth-msg">Onbekende toestand</p>';
  }

  function bindHandlers() {
    document.getElementById('setup-signout')?.addEventListener('click', async () => {
      await signOut();
    });

    if (view === 'choose') {
      const form = document.getElementById('setup-form-choose');
      const nameInput = document.getElementById('setup-name');
      nameInput.focus();

      form.querySelectorAll('[data-action]').forEach(btn => {
        btn.addEventListener('click', () => {
          const name = nameInput.value.trim();
          if (!name) {
            nameInput.focus();
            return;
          }
          chosenName = name;
          view = btn.dataset.action === 'go-create' ? 'create' : 'join';
          render();
        });
      });
    }

    if (view === 'create') {
      const form = document.getElementById('setup-form-create');
      const nameInput = document.getElementById('setup-household-name');
      nameInput.focus();

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const householdName = nameInput.value.trim() || `Huishouden van ${chosenName}`;
        const msg = document.getElementById('setup-msg');
        const submitBtn = form.querySelector('button[type="submit"]');
        submitBtn.disabled = true;
        submitBtn.textContent = 'Aanmaken…';
        msg.className = 'auth-msg';
        msg.textContent = '';
        try {
          // Eén RPC-call: huishouden aanmaken + linken aan profile + display_name updaten.
          // Zie supabase.js setupNewHousehold-comment voor waarom dit één functie is.
          createdHousehold = await setupNewHousehold(chosenName, householdName);
          view = 'invite-shown';
          render();
        } catch (err) {
          msg.className = 'auth-msg auth-msg-error';
          msg.textContent = 'Aanmaken mislukt: ' + (err?.message || 'onbekende fout');
          submitBtn.disabled = false;
          submitBtn.textContent = 'Maak aan';
        }
      });

      form.querySelector('[data-action="back"]').addEventListener('click', () => {
        view = 'choose';
        render();
      });
    }

    if (view === 'join') {
      const form = document.getElementById('setup-form-join');
      const inviteInput = document.getElementById('setup-invite');
      inviteInput.focus();
      // Selecteer de hele waarde zodat de gebruiker direct kan overtypen
      inviteInput.select();

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const raw = inviteInput.value.trim();
        const householdId = extractUuid(raw);
        const msg = document.getElementById('setup-msg');
        const submitBtn = form.querySelector('button[type="submit"]');
        msg.className = 'auth-msg';
        msg.textContent = '';
        if (!householdId) {
          msg.className = 'auth-msg auth-msg-error';
          msg.textContent = 'Dat ziet er niet uit als een geldige uitnodigingscode. Vraag je medebewoner om de link opnieuw te sturen.';
          return;
        }
        submitBtn.disabled = true;
        submitBtn.textContent = 'Aansluiten…';
        try {
          await updateProfile(profile.id, {
            display_name: chosenName,
            household_id: householdId
          });
          // Bevestig met fetchHousehold — als RLS bij dat user toelaat de
          // households-rij te lezen, dan zit hij correct in. Anders → fout.
          const household = await fetchHousehold(householdId);
          onDone({ ...profile, display_name: chosenName, household_id: householdId }, household);
        } catch (err) {
          msg.className = 'auth-msg auth-msg-error';
          msg.textContent = 'Aansluiten mislukt: ' + (err?.message || 'onbekende fout');
          submitBtn.disabled = false;
          submitBtn.textContent = 'Aansluiten';
        }
      });

      form.querySelector('[data-action="back"]').addEventListener('click', () => {
        view = 'choose';
        render();
      });
    }

    if (view === 'invite-shown') {
      document.querySelector('[data-action="copy-link"]')?.addEventListener('click', async () => {
        const linkInput = document.getElementById('setup-invite-link');
        try {
          await navigator.clipboard.writeText(linkInput.value);
          const btn = document.querySelector('[data-action="copy-link"]');
          const oldText = btn.textContent;
          btn.textContent = '✓ Gekopieerd';
          setTimeout(() => { btn.textContent = oldText; }, 1500);
        } catch {
          linkInput.select();
          document.execCommand('copy');
        }
      });
      document.querySelector('[data-action="finish"]')?.addEventListener('click', () => {
        onDone(
          { ...profile, display_name: chosenName, household_id: createdHousehold.id },
          createdHousehold
        );
      });
    }
  }

  render();
}

// ---------- Hulpjes ----------

const UUID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** Haal een UUID uit een string die ofwel puur de UUID, ofwel een hele
 *  uitnodigingslink met ?invite=UUID erin bevat. */
function extractUuid(input) {
  if (!input) return null;
  const m = String(input).match(UUID_REGEX);
  return m ? m[0].toLowerCase() : null;
}

/** Bouw de uitnodigings-URL voor een gegeven household. */
function inviteLinkFor(householdId) {
  const url = new URL(window.location.origin);
  url.searchParams.set('invite', householdId);
  return url.toString();
}

/** Suggereer een vriendelijke default-naam voor het huishouden op basis
 *  van de display name. "Lander" → "Lander & co". */
function suggestHouseholdName(displayName) {
  if (!displayName) return '';
  return `${displayName} & co`;
}

/** Escape voor gebruik binnen een HTML-attribuutwaarde. */
function escapeAttr(str) {
  return String(str || '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}
